import { useState, useCallback } from 'react';
import { FilteredSegment, FilteringStats } from '../services/ContentFilteringService';
import { useToast } from '../components/Toast';

export const useSessionManagement = () => {
  const toast = useToast();
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionTime, setSessionTime] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState<string | null>(null);
  const [sessionNotes, setSessionNotes] = useState('');

  const startSession = useCallback((patientName?: string) => {
    setSessionActive(true);
    setSessionTime(0);
    if (patientName) {
      setSelectedPatient(patientName);
    }
  }, []);

  const endSession = useCallback(() => {
    setSessionActive(false);
    setSelectedPatient(null);
    setSessionNotes('');
    toast.success('Session Completed', `Duration: ${Math.floor(sessionTime / 60)}:${(sessionTime % 60).toString().padStart(2, '0')}\nSession notes saved.`);
  }, [sessionTime]);

  const downloadRecordingFiles = useCallback(async (
    originalBlob: Blob,
    filteredBlob: Blob,
    sessionData: any,
    transcription?: any
  ) => {
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const patientName = (sessionData.patient || 'Unknown').replace(/[^a-zA-Z0-9]/g, '_');

      // Download original audio
      const originalUrl = URL.createObjectURL(originalBlob);
      downloadFile(originalUrl, `${patientName}_${timestamp}_original.webm`);

      // Download filtered audio
      const filteredUrl = URL.createObjectURL(filteredBlob);
      downloadFile(filteredUrl, `${patientName}_${timestamp}_filtered.wav`);

      // Download session summary (JSON)
      const summaryBlob = new Blob([JSON.stringify(sessionData, null, 2)], { type: 'application/json' });
      const summaryUrl = URL.createObjectURL(summaryBlob);
      downloadFile(summaryUrl, `${patientName}_${timestamp}_session.json`);

      // Download transcription (TXT)
      if (transcription?.fullText) {
        const transcriptBlob = new Blob([transcription.fullText], { type: 'text/plain' });
        const transcriptUrl = URL.createObjectURL(transcriptBlob);
        downloadFile(transcriptUrl, `${patientName}_${timestamp}_transcription.txt`);
      }

      // Download filtered transcription if available
      if (transcription?.filteredSegments) {
        const filteredText = transcription.filteredSegments
          .filter((seg: any) => seg.isRelevant && seg.filteredText)
          .map((seg: any) => seg.filteredText)
          .join(' ');

        if (filteredText) {
          const filteredTranscriptBlob = new Blob([filteredText], { type: 'text/plain' });
          const filteredTranscriptUrl = URL.createObjectURL(filteredTranscriptBlob);
          downloadFile(filteredTranscriptUrl, `${patientName}_${timestamp}_filtered_transcription.txt`);
        }
      }

      // Download full report if filtering was applied
      if (transcription?.filteredSegments) {
        const report = generateSessionReport(sessionData, transcription);
        const reportBlob = new Blob([report], { type: 'text/plain' });
        const reportUrl = URL.createObjectURL(reportBlob);
        downloadFile(reportUrl, `${patientName}_${timestamp}_full_report.txt`);
      }

    } catch (error) {
      console.error('Failed to download files:', error);
      toast.error('Download Failed', 'Some files could not be downloaded. Please try again.');
    }
  }, []);

  const handleRecordingComplete = useCallback(async (
    originalBlob: Blob,
    filteredBlob: Blob,
    metadata: any,
    transcription?: any
  ) => {
    try {
      const sessionId = `session_${Date.now()}`;
      const patientId = selectedPatient && !selectedPatient.includes('Meeting') ? selectedPatient : undefined;

      const sessionData = {
        sessionId,
        patientId,
        sessionNotes,
        metadata,
        transcription,
        timestamp: new Date().toISOString(),
        duration: metadata.duration,
        patient: selectedPatient
      };

      let alertMessage = `Recording completed successfully!

Duration: ${Math.floor(metadata.duration / 60)}:${(metadata.duration % 60).toString().padStart(2, '0')}
File Size: ${Math.round(metadata.fileSize / 1024)} KB
Filtered Size: ${Math.round(metadata.filteredFileSize / 1024)} KB
Silence Removed: ${metadata.silenceRemoved}%
Noise Reduction: ${metadata.noiseReduction ? 'Yes' : 'No'}`;

      if (transcription) {
        alertMessage += `\nTranscription: ${transcription.totalWords} words (${Math.round(transcription.avgConfidence * 100)}% confidence)`;

        if (transcription.filteringStats) {
          const stats = transcription.filteringStats;
          alertMessage += `\n\nContent Filtering Results:`;
          alertMessage += `\n• Relevant segments: ${stats.relevantSegments}/${stats.totalSegments}`;
          alertMessage += `\n• Medical content: ${stats.medicalContent} segments`;
          alertMessage += `\n• Average relevance: ${Math.round(stats.averageRelevanceScore * 100)}%`;

          if (stats.offTopicContent > 0 || stats.interruptionsFiltered > 0) {
            alertMessage += `\n• Filtered out: ${stats.offTopicContent} off-topic, ${stats.interruptionsFiltered} interruptions`;
          }
        }
      }

      alertMessage += `\n\nFiles are ready for download!`;

      setTimeout(() => {
        downloadRecordingFiles(originalBlob, filteredBlob, sessionData, transcription);
      }, 1000);

      toast.success('Recording Processed', alertMessage);

    } catch (error) {
      console.error('Failed to process recording:', error);
      toast.error('Processing Failed', 'Failed to process recording. Please try again.');
    }
  }, [selectedPatient, sessionNotes, downloadRecordingFiles]);

  const handleRecordingStart = useCallback(() => {
    // Recording started
  }, []);

  const handleRecordingStop = useCallback(() => {
    // Recording stopped
  }, []);

  const handleContentFiltered = useCallback((filteredSegments: FilteredSegment[], stats: FilteringStats) => {
    // Content filtering results available via stats parameter
  }, []);


  const downloadFile = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const generateSessionReport = (sessionData: any, transcription: any): string => {
    let report = `SESSION REPORT\n`;
    report += `${'='.repeat(50)}\n\n`;

    report += `Session Information:\n`;
    report += `• Session ID: ${sessionData.sessionId}\n`;
    report += `• Patient: ${sessionData.patient || 'Unknown'}\n`;
    report += `• Date: ${new Date(sessionData.timestamp).toLocaleString()}\n`;
    report += `• Duration: ${Math.floor(sessionData.duration / 60)}:${(sessionData.duration % 60).toString().padStart(2, '0')}\n`;

    if (sessionData.sessionNotes) {
      report += `• Notes: ${sessionData.sessionNotes}\n`;
    }

    report += `\nAudio Processing:\n`;
    report += `• Original size: ${Math.round(sessionData.metadata.fileSize / 1024)} KB\n`;
    report += `• Filtered size: ${Math.round(sessionData.metadata.filteredFileSize / 1024)} KB\n`;
    report += `• Silence removed: ${sessionData.metadata.silenceRemoved}%\n`;
    report += `• Noise reduction: ${sessionData.metadata.noiseReduction ? 'Yes' : 'No'}\n`;

    if (transcription) {
      report += `\nTranscription:\n`;
      report += `• Total words: ${transcription.totalWords}\n`;
      report += `• Average confidence: ${Math.round(transcription.avgConfidence * 100)}%\n`;
      report += `• Total segments: ${transcription.segments?.length || 0}\n`;

      if (transcription.filteringStats) {
        const stats = transcription.filteringStats;
        report += `\nContent Filtering:\n`;
        report += `• Relevant segments: ${stats.relevantSegments}/${stats.totalSegments}\n`;
        report += `• Medical content: ${stats.medicalContent} segments\n`;
        report += `• Average relevance: ${Math.round(stats.averageRelevanceScore * 100)}%\n`;
        report += `• Off-topic filtered: ${stats.offTopicContent}\n`;
        report += `• Interruptions filtered: ${stats.interruptionsFiltered}\n`;
        report += `• Processing time: ${stats.processingTime}ms\n`;
      }

      report += `\nFull Transcription:\n`;
      report += `${'-'.repeat(30)}\n`;
      report += `${transcription.fullText}\n\n`;

      if (transcription.filteredSegments) {
        const relevantText = transcription.filteredSegments
          .filter((seg: any) => seg.isRelevant && seg.filteredText)
          .map((seg: any) => seg.filteredText)
          .join(' ');

        if (relevantText && relevantText !== transcription.fullText) {
          report += `Filtered Transcription (Medical Content Only):\n`;
          report += `${'-'.repeat(30)}\n`;
          report += `${relevantText}\n\n`;
        }
      }
    }

    return report;
  };

  return {
    sessionActive,
    sessionTime,
    selectedPatient,
    sessionNotes,
    setSessionTime,
    setSessionNotes,
    startSession,
    endSession,
    handleRecordingComplete,
    handleRecordingStart,
    handleRecordingStop,
    handleContentFiltered
  };
};